import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Buildings,
  Sparkle,
  ClipboardText,
  Users,
  Plant,
  Clipboard,
  Clock,
  MapPin,
  ShieldCheck,
  HandHeart,
  Path,
  CheckCircle,
  ArrowLeft,
} from '@phosphor-icons/react';

// Local bundled service images — served from the same origin as the app, so
// they can never 404 due to CDN issues or DNS/caching quirks. Webpack fingerprints
// each file, giving us automatic cache-busting on every deploy.
// Original assets from the central Assets Library. Each service card gets a
// thematically distinct image; there is no external CDN dependency and no
// copyright encumbrance.
import { libraryImages } from '../assets/imageLibrary';

const imgEducational = libraryImages.saudiNatureLearningCorners; // inquiry consulting
const imgAdministrative = libraryImages.kindergartenCorridor; // premium operations / interior
const imgQuality = libraryImages.documentationPlantsAr;       // Arabic quality docwall
const imgKindergarten = libraryImages.saudiOutdoorClimbing;   // Saudi outdoor kindergarten
const imgPolicy = libraryImages.dailyPlanBoard;               // policies / structured daily routines

const SERVICES = [
  {
    id: 'educational-consulting',
    icon: Plant,
    title: 'الاستشارات التربوية وتطوير تجربة الطفل',
    description:
      'خدمة استشارية قائمة على تشخيص وتحليل واقع الممارسات التربوية داخل المنشأة، وتقديم توصيات وحلول تربوية مصمَّمة بعناية. نُحلّل الممارسات الصفية القائمة على اللعب والاستقصاء، ونقرأ بيئات التعلم وأدوات التقييم، ثم نُصمّم توصيات قابلة للتطبيق تعكس فلسفة المنشأة وتخدم النمو التكاملي للطفل. النتيجة: قراءة معمّقة لواقع التجربة التربوية، وحلول عملية موجّهة نحو رفع جودة تجربة الطفل اليومية.',
    bullets: [
      'تقييم البيئة التعليمية والممارسات الصفية',
      'تطوير تجربة الطفل اليومية',
      'بناء توصيات قابلة للتطبيق',
    ],
    image: imgEducational,
    includes: [
      'تشخيص تجربة الطفل داخل البيئة التعليمية',
      'تحليل وتصميم توصيات للبيئات التعليمية',
      'تحليل الممارسات التربوية وتطويرها',
      'تصميم مقترحات للأنشطة وتجارب التعلم',
      'قراءة جودة التفاعل بين المعلم والطفل',
      'توصيات لدعم التعلم القائم على اللعب والاستقصاء',
      'بناء خطط التحسين التربوي المبنية على فلسفة المنهج المحدد',
    ],
    audience: ['الحضانات', 'رياض الأطفال', 'الصفوف الأولية'],
    process: [
      'زيارة تشخيصية وملاحظة',
      'مقابلات مع الكادر والقيادة',
      'تقرير وقراءة معمقة',
      'جلسة عرض النتائج والتوصيات',
    ],
    timeline: 'من ٢ إلى ٤ أسابيع',
    fieldVisit: true,
  },
  {
    id: 'administrative-consulting',
    icon: ClipboardText,
    title: 'الاستشارات الإدارية',
    description:
      'نتعامل مع الفجوات الإدارية التي تُبطئ أداء المنشأة: تداخل الأدوار، بطء اتخاذ القرار، وضعف كفاءة التشغيل اليومي. نراجع الهيكل التنظيمي والنماذج التشغيلية، ونُعيد تصميمها بما يناسب حجم المنشأة وطبيعة عملها، ثم نرافق الفريق حتى مرحلة التطبيق والمتابعة. النتيجة: قرارات أسرع، أدوار أوضح، وأداء تشغيلي أعلى كفاءة.',
    bullets: [
      'مراجعة الهيكل التنظيمي',
      'تصميم النماذج التشغيلية',
      'تطوير مسارات اتخاذ القرار',
    ],
    image: imgAdministrative,
    includes: [
      'مراجعة الهيكل التنظيمي وتوصيف الأدوار',
      'تصميم وتطوير نماذج العمل والإجراءات الإدارية',
      'تحسين كفاءة التشغيل اليومي',
      'تطوير مؤشرات الأداء الإدارية',
    ],
    audience: ['الحضانات', 'رياض الأطفال', 'المجمعات التعليمية', 'المشاريع المؤسسية'],
    process: [
      'لقاء استكشافي مع القيادة',
      'تشخيص الواقع الإداري',
      'إعداد توصيات منظمة',
      'دعم التطبيق وقياس التحسن',
    ],
    timeline: 'من ٤ إلى ٨ أسابيع',
    fieldVisit: false,
  },
  {
    id: 'quality-accreditation',
    icon: Sparkle,
    title: 'الجودة والتطوير',
    description:
      'نبني معكم منظومة جودة عملية داخل المنشأة تبدأ بتقييم الواقع، وتحليل الفجوات، وتحديد أولويات التحسين، ثم إعداد خطة تطوير قابلة للتنفيذ. نُرافق الفريق حتى مرحلة التطبيق، ونُهيّئ المنشأة لجاهزية الاعتماد الوطني والدولي عند الحاجة، ونربطها بالجهات المعنية. النتيجة: منظومة جودة مستدامة تُقاس وتُطوَّر وتُثبَت وفق معايير معتمدة.',
    bullets: [
      'إعداد وتنفيذ خطط التحسين',
      'تحليل الفجوات وتشخيصها',
      'تجهيز الوثائق والملفات',
      'محاكاة زيارة التقييم',
    ],
    image: imgQuality,
    includes: [
      'تقييم جودة الأداء',
      'تحليل الفجوات',
      'خطط التحسين',
      'تقييم تجربة الأسرة',
      'الجاهزية للتقييمات الخارجية',
      'التطوير المستمر',
    ],
    capabilitiesTitle: 'بناء القدرات المؤسسية واستدامة الجودة',
    capabilities: [
      'إعداد خطط التطوير المهني وبناء القدرات وفق الاحتياج',
      'قيادة التغيير والتحول نحو التجويد',
      'إعداد الأدلة المهنية وجمع الشواهد',
      'تقييم البيئة التعليمية والصفية',
      'تعزيز ممارسات الدعم والرعاية الشاملة للطفل',
      'ترسيخ ممارسات حماية الطفل',
      'التعليم المتمايز — للأطفال المتميزين والأطفال الأولى بالرعاية',
      'توظيف التعلم الرقمي في الطفولة المبكرة',
    ],
    audience: ['الحضانات', 'رياض الأطفال', 'المدارس العالمية', 'المجمعات التعليمية'],
    process: [
      'لقاء استكشافي لفهم وضع المنشأة',
      'تشخيص أولي وقراءة الفجوات',
      'خطة جاهزية بمؤشرات قياس واضحة',
      'دعم التطبيق + قياس التقدم والأثر',
    ],
    timeline: 'من ٦ إلى ١٢ أسبوعاً حسب نطاق العمل',
    fieldVisit: true,
    quotationServiceId: 'accreditation',
  },
  {
    id: 'nonprofit-sector',
    icon: HandHeart,
    title: 'خدمات القطاع غير الربحي',
    en: 'Nonprofit Sector Services',
    description:
      'حلول متكاملة للجمعيات والمؤسسات الأهلية ودور تحفيظ القرآن الكريم — من دراسة الاحتياج التربوي إلى تصميم وتطوير نماذج وبرامج الطفولة المبكرة باستدامة وحوكمة رشيدة.',
    bullets: [
      'تصميم برامج الطفولة المبكرة',
      'حوكمة العمل الأهلي والاستدامة',
      'بناء القدرات المؤسسية للكادر',
    ],
    image: imgKindergarten,
    includes: [
      'دراسة الاحتياج المجتمعي والتربوي',
      'تصميم وتطوير نماذج وبرامج الطفولة المبكرة',
      'حوكمة العمل الأهلي والاستدامة',
      'بناء قدرات الكادر التربوي والإداري',
      'تطوير الشراكات المؤسسية والداعمين',
      'قياس الأثر الاجتماعي والتربوي',
    ],
    audience: ['الجمعيات الأهلية', 'المؤسسات غير الربحية', 'دور تحفيظ القرآن الكريم', 'مبادرات المسؤولية الاجتماعية'],
    process: [
      'الاستكشاف وتحديد الاحتياج',
      'التصميم التربوي وإعداد النموذج التشغيلي',
      'بناء القدرات والتأهيل',
      'دعم التطبيق وقياس الأثر',
    ],
    supportNote: {
      title: 'دعم القطاع غير الربحي',
      body:
        'نؤمن بأن تنمية القطاع غير الربحي شريك أساسي في تطوير منظومة الطفولة المبكرة في المملكة؛ لذلك نوفر للجهات غير الربحية نموذج تسعير داعمًا ومرنًا يراعي حجم المنشأة ونطاق العمل، بما يسهم في توسيع أثرها واستدامة خدماتها.',
    },
    timeline: 'من ٦ أسابيع إلى ٥ أشهر',
    fieldVisit: true,
    quotationServiceId: 'nonprofit-sector',
  },
  {
    id: 'learning-pathways',
    icon: Path,
    title: 'استحداث وتطوير المسارات التعليمية',
    en: 'Learning Pathway Development',
    description:
      'تصميم واستحداث مسارات تعليمية للطفولة المبكرة مبنية على أعمق الفلسفات التربوية، وبتكيّف واعٍ مع السياق السعودي والخليجي ومتطلبات الجودة الوطنية.',
    bullets: [
      'خرائط تعلم متدرّجة وواضحة',
      'دمج المناهج العالمية بسياقنا',
      'أدوات تقييم متمركزة حول الطفل',
    ],
    image: imgEducational,
    includes: [
      'تحليل الاحتياج التعليمي للفئة العمرية',
      'اختيار الفلسفة التربوية الأنسب والتوصية بها',
      'بناء خريطة المسار التعليمي بمخرجاتها',
      'تصميم الأنشطة والوحدات التعلّمية',
      'تطوير أدوات التقييم المتمركز حول الطفل',
      'إعداد خطة تطوير ودعم التطبيق وقياس الأثر',
    ],
    audience: ['رياض الأطفال والحضانات', 'المؤسسات التعليمية', 'الجهات المطوّرة للمناهج', 'المشروعات الجديدة'],
    process: [
      'تحليل السياق والفئة المستهدفة',
      'التصميم المفاهيمي للمسار',
      'التطوير التفصيلي والتوثيق',
      'دعم التطبيق وقياس الأثر',
    ],
    timeline: 'من ٨ إلى ١٦ أسبوعاً',
    fieldVisit: true,
    quotationServiceId: 'learning-pathways',
  },
];

const ServicesSection = () => {
  const [openId, setOpenId] = useState(null);
  const open = openId ? SERVICES.find((s) => s.id === openId) : null;
  const OpenIcon = open?.icon;

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-20 bg-[#FAFAF7]"
      dir="rtl"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          className="max-w-3xl mb-14 md:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center gap-2 text-[11px] font-medium tracking-[0.2em] text-[#4A5D23] uppercase mb-5">
            <span className="w-8 h-px bg-[#4A5D23]" />
            الخدمات الاستشارية
          </span>
          <h2 className="font-['Noto_Kufi_Arabic'] font-extrabold text-[#1F3D2B] text-[26px] sm:text-[32px] md:text-[40px] lg:text-[46px] leading-[1.35] tracking-[-0.005em] mb-5">
            خدمات متخصصة لمنشات الطفولة المبكرة
          </h2>
          <p className="text-[#3D512D] text-[15px] sm:text-lg leading-9 max-w-2xl">
            نرافقكم من التشخيص الأولي إلى التطبيق العملي، بأدوات منظمة وقراءة
            ميدانية، وفق منهجية استشارية مؤسسية.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.id}
                data-testid={`consulting-card-${s.id}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.5 }}
                className="bg-white rounded-[20px] border border-[#E6E0D6] overflow-hidden flex flex-col shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="relative h-44 overflow-hidden bg-[#F5F2EC]">
                  <img
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Graceful fallback: hide the broken icon and paint a
                      // calm neutral tone so the layout stays intact even if a
                      // remote asset ever 404s in production.
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.parentElement.style.background =
                        'linear-gradient(135deg,#E8E4DA 0%,#D9D3C5 100%)';
                    }}
                  />
                  <div className="absolute top-3 right-3 w-10 h-10 rounded-xl bg-white/95 flex items-center justify-center">
                    <Icon size={20} weight="duotone" className="text-[#1F3D2B]" />
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-['Noto_Kufi_Arabic'] font-bold text-[#1F3D2B] text-lg mb-2 leading-tight tracking-tight">
                    {s.title}
                  </h3>
                  {s.comingSoon && (
                    <span
                      data-testid={`coming-soon-${s.id}`}
                      className="self-start inline-block bg-[#F5F2EC] text-[#7A866E] text-[11px] font-['Noto_Kufi_Arabic'] px-2.5 py-0.5 rounded-full border border-[#E6E0D6] mb-3"
                    >
                      {s.comingSoonLabel || 'قيد الإعداد'}
                    </span>
                  )}
                  <p className="text-sm text-[#6B7565] leading-7 mb-4">
                    {s.description}
                  </p>
                  <ul className="space-y-1.5 mb-5">
                    {s.bullets.slice(0, 3).map((b, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-1.5 text-sm text-[#3D512D]"
                      >
                        <span className="w-1 h-1 mt-2.5 rounded-full bg-[#4A641F] shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-auto pt-4 border-t border-[#E6E0D6]">
                    <p className="text-xs text-[#92A082] mb-3">
                      {s.comingSoon
                        ? 'سيتم إطلاق الخدمة قريبًا'
                        : 'عرض سعر مخصص حسب نطاق العمل'}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button
                        data-testid={`details-btn-${s.id}`}
                        onClick={() => setOpenId(s.id)}
                        className="flex-1 border border-[#1F3D2B] text-[#1F3D2B] hover:bg-[#1F3D2B] hover:text-white rounded-xl py-2 text-sm font-medium transition-colors"
                      >
                        تفاصيل الخدمة
                      </button>
                      {s.comingSoon ? (
                        <span
                          data-testid={`quote-btn-${s.id}-disabled`}
                          aria-disabled="true"
                          className="flex-1 bg-[#D8D2C8] text-[#7A866E] rounded-xl py-2 text-sm font-medium text-center cursor-not-allowed select-none"
                        >
                          قيد الإعداد
                        </span>
                      ) : (
                        <Link
                          to={s.actionHref || `/quotation?service=${s.quotationServiceId || s.id}`}
                          data-testid={`quote-btn-${s.id}`}
                          className="flex-1 bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl py-2 text-sm font-medium transition-colors text-center"
                        >
                          {s.actionLabel || 'اطلب عرض سعر'}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>

        <p className="text-center text-xs sm:text-sm text-[#7A866E] mt-8">
          يمكنكم طلب عرض سعر للخدمة أو حجز جلسة استشارية من داخل تفاصيل الخدمة.
        </p>
      </div>

      {/* Service details modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/55 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setOpenId(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.97 }}
              className="bg-white rounded-[24px] max-w-2xl w-full p-6 sm:p-8 my-8 relative max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              dir="rtl"
              data-testid="service-details-modal"
            >
              <button
                onClick={() => setOpenId(null)}
                aria-label="إغلاق"
                className="absolute top-4 left-4 text-[#7A866E] hover:text-[#1F3D2B] transition-colors"
              >
                <X size={24} />
              </button>

              <div className="flex items-start gap-3 mb-5 pe-6">
                <div className="w-12 h-12 rounded-2xl bg-[#F5F2EC] flex items-center justify-center shrink-0">
                  {OpenIcon && (
                    <OpenIcon size={24} weight="duotone" className="text-[#1F3D2B]" />
                  )}
                </div>
                <div>
                  <h3 className="font-['Noto_Kufi_Arabic'] font-bold text-[#1F3D2B] text-xl sm:text-2xl tracking-tight">
                    {open.title}
                  </h3>
                  {open.comingSoon && (
                    <span className="inline-block bg-[#F5F2EC] text-[#7A866E] text-[11px] font-['Noto_Kufi_Arabic'] px-2.5 py-0.5 rounded-full border border-[#E6E0D6] mt-2">
                      {open.comingSoonLabel || 'قيد الإعداد'}
                    </span>
                  )}
                  <p className="text-sm text-[#92A082] mt-1">
                    {open.comingSoon
                      ? 'سيتم إطلاق الخدمة قريبًا'
                      : 'عرض سعر مخصص حسب نطاق العمل'}
                  </p>
                </div>
              </div>

              <p className="text-[#3D512D] leading-8 mb-5">{open.description}</p>

              <div className="space-y-5 mb-6">
                <Block
                  icon={Clipboard}
                  title="ما تشمله الخدمة"
                  items={open.includes}
                />
                {open.capabilities && (
                  <Block
                    icon={Sparkle}
                    title={open.capabilitiesTitle || 'محاور بناء القدرات'}
                    items={open.capabilities}
                  />
                )}
                <Block
                  icon={Users}
                  title="الفئة المستهدفة"
                  items={open.audience}
                />
                <Block icon={Plant} title="كيف نعمل" items={open.process} />
                <div className="grid sm:grid-cols-2 gap-3 bg-[#F5F2EC] rounded-xl p-4">
                  <div className="flex items-center gap-2 text-sm text-[#1F3D2B]">
                    <Clock size={18} className="text-[#4A641F]" />
                    <span>
                      المدة المتوقعة: <strong>{open.timeline}</strong>
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#1F3D2B]">
                    <MapPin size={18} className="text-[#4A641F]" />
                    <span>
                      الزيارة الميدانية:{' '}
                      <strong>
                        {open.fieldVisit
                          ? 'قد تكون مطلوبة حسب الاحتياج'
                          : 'عن بعد بشكل أساسي'}
                      </strong>
                    </span>
                  </div>
                </div>
              </div>

              {open.outcomes && open.outcomes.length > 0 && (
                <div className="mb-6 bg-[#F5F2EC] border border-[#E6E0D6] rounded-xl p-4">
                  <Block
                    icon={HandHeart}
                    title="النتيجة التي ستحصل عليها"
                    items={open.outcomes}
                  />
                </div>
              )}

              {open.supportNote && (
                <div
                  data-testid={`support-note-${open.id}`}
                  className="mb-6 bg-[#F5F2EC] border border-[#E6E0D6] rounded-xl p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shrink-0">
                      <HandHeart size={18} weight="duotone" className="text-[#4A641F]" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-[#1F3D2B] mb-1.5 font-['IBM_Plex_Sans_Arabic']">
                        {open.supportNote.title}
                      </p>
                      <p className="text-sm text-[#3D512D] leading-8">
                        {open.supportNote.body}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-[#E6E0D6]">
                {open.comingSoon ? (
                  <span
                    data-testid="modal-quote-btn-disabled"
                    aria-disabled="true"
                    className="flex-1 bg-[#D8D2C8] text-[#7A866E] rounded-xl py-3 text-sm font-bold flex items-center justify-center gap-2 cursor-not-allowed select-none"
                  >
                    {open.comingSoonLabel || 'قيد الإعداد'} — سيتم الإطلاق قريبًا
                  </span>
                ) : (
                  <Link
                    to={open.actionHref || `/quotation?service=${open.quotationServiceId || open.id}`}
                    data-testid="modal-quote-btn"
                    onClick={() => setOpenId(null)}
                    className="flex-1 bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl py-3 text-sm font-bold flex items-center justify-center gap-2"
                  >
                    {open.actionLabel || 'اطلب عرض سعر'}
                    <ArrowLeft size={16} className="rtl-flip" />
                  </Link>
                )}
                <Link
                  to="/booking"
                  data-testid="modal-booking-btn"
                  onClick={() => setOpenId(null)}
                  className="flex-1 border border-[#1F3D2B] text-[#1F3D2B] hover:bg-[#F5F2EC] rounded-xl py-3 text-sm font-bold flex items-center justify-center gap-2"
                >
                  احجز جلسة
                </Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

const Block = ({ icon: Icon, title, items }) => (
  <div>
    <h4 className="font-bold text-[#1F3D2B] mb-2 flex items-center gap-2">
      <Icon size={18} weight="duotone" className="text-[#4A641F]" />
      {title}
    </h4>
    <ul className="space-y-1.5 pr-4">
      {items.map((it, idx) => (
        <li key={idx} className="flex items-start gap-2 text-sm text-[#3D512D]">
          <CheckCircle size={14} weight="fill" className="text-[#4A641F] mt-1 shrink-0" />
          {it}
        </li>
      ))}
    </ul>
  </div>
);

export default ServicesSection;
